import { API_CONFIG, DEFAULT_HEADERS } from "../config/api";

// Helper function to handle session expiration
// JWT tokens expire after 24 hours - when expired, users must log in again
const handleSessionExpiration = () => {
  // Clear auth data from localStorage
  localStorage.removeItem('authToken');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('userBranches');
  localStorage.removeItem('userMenuPermissions');
  
  // Redirect to login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Standard API response structures used across services
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp?: string;
  pagination?: PaginationInfo;

}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export class BaseApiService {
  /**
   * Get the JWT token from localStorage
   */
  protected getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Build headers with JWT token if available
   */
  protected buildHeaders(customHeaders?: HeadersInit): HeadersInit {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };

    // Merge custom headers first
    if (customHeaders) {
      Object.assign(headers, customHeaders);
    }

    // Add Authorization header with JWT token if available and not already set
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  protected async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const requestId = Math.random().toString(36).substr(2, 9);
    const startTime = Date.now();
    
    try {
      const url = `${API_CONFIG.BASE_URL}${endpoint}`;
      
      // Build headers with token - merge with any existing headers from options
      const headers = this.buildHeaders(options.headers);
      
      // Remove headers from options to avoid overwriting our built headers
      const { headers: _, ...restOptions } = options;
      
      const response = await fetch(url, {
        ...restOptions,
        headers,
      });

      const responseTime = Date.now() - startTime;
      // Debug logs removed

      if (!response.ok) {
        // Check for session expiration (401 Unauthorized or 403 Forbidden)
        // Both indicate the user needs to log in again
        if (response.status === 401 || response.status === 403) {
          handleSessionExpiration();
          const error = new Error('Session expired. Please login again.') as any;
          error.status = response.status;
          error.isSessionExpired = true;
          throw error;
        }
        
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage += ` - ${errorData.message}`;
          }
          if (errorData.error) {
            errorMessage += ` - ${errorData.error}`;
          }
        } catch (parseError) {
          // If we can't parse the error response, use the default message
        }
        // Logging removed
        const error = new Error(errorMessage) as any;
        error.status = response.status;
        throw error;
      }

      const jsonData = await response.json();
      const totalTime = Date.now() - startTime;
      // Debug logs removed
      
      return jsonData;
    } catch (error) {
      const totalTime = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : String(error);
      // console.error(`💥 [${requestId}] API Request ERROR:`, {
      //   error: errorMessage,
      //   totalTime: `${totalTime}ms`,
      //   url: `${API_CONFIG.BASE_URL}${endpoint}`,
      //   timestamp: new Date().toISOString()
      // });
      throw error;
    }
  }

  protected async makeRequestWithTimeout<T>(
    endpoint: string,
    options: RequestInit = {},
    timeout: number = API_CONFIG.TIMEOUT
  ): Promise<T> {
    const requestId = Math.random().toString(36).substr(2, 9);
    const startTime = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const url = `${API_CONFIG.BASE_URL}${endpoint}`;
      // Debug logs removed
      
      // Build headers with token - merge with any existing headers from options
      const headers = this.buildHeaders(options.headers);
      
      // Remove headers from options to avoid overwriting our built headers
      const { headers: _, ...restOptions } = options;
      
      const response = await fetch(url, {
        ...restOptions,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      // Debug logs removed

      if (!response.ok) {
        // Check for session expiration (401 Unauthorized or 403 Forbidden)
        // Both indicate the user needs to log in again
        if (response.status === 401 || response.status === 403) {
          handleSessionExpiration();
          const error = new Error('Session expired. Please login again.') as any;
          error.status = response.status;
          error.isSessionExpired = true;
          throw error;
        }
        
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage += ` - ${errorData.message}`;
          }
          if (errorData.error) {
            errorMessage += ` - ${errorData.error}`;
          }
        } catch (parseError) {
          // If we can't parse the error response, use the default message
        }
        // Logging removed
        const error = new Error(errorMessage) as any;
        error.status = response.status;
        throw error;
      }

      const jsonData = await response.json();
      const totalTime = Date.now() - startTime;
      // Debug logs removed
      
      return jsonData;
    } catch (error) {
      clearTimeout(timeoutId);
      const totalTime = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isTimeout = error instanceof Error && error.name === 'AbortError';
      // console.error(`💥 [${requestId}] API Request with Timeout ERROR:`, {
      //   error: errorMessage,
      //   totalTime: `${totalTime}ms`,
      //   url: `${API_CONFIG.BASE_URL}${endpoint}`,
      //   isTimeout,
      //   timestamp: new Date().toISOString()
      // });
      throw error;
    }
  }

  protected async retryRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retries: number = API_CONFIG.RETRY_ATTEMPTS
  ): Promise<T> {
    const requestId = Math.random().toString(36).substr(2, 9);
    let lastError: Error;

    // Debug logs removed

    for (let i = 0; i < retries; i++) {
      try {
        // Debug logs removed
        return await this.makeRequest<T>(endpoint, options);
      } catch (error) {
        lastError = error as Error;
        const errorMessage = error instanceof Error ? error.message : String(error);
        // Debug logs removed
        
        if (i === retries - 1) {
          // console.error(`💥 [${requestId}] All retry attempts exhausted:`, {
          //   endpoint,
          //   totalAttempts: retries,
          //   finalError: lastError.message
          // });
          throw lastError;
        }
        
        const backoffTime = Math.pow(2, i) * 1000;
        // Debug logs removed
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, backoffTime));
      }
    }

    throw lastError!;
  }
} 